import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
  MatLegacyDialogModule,
} from '@angular/material/legacy-dialog';
import { Task } from '../../legacy-tasks/tasks.types';
import { FormsModule } from '@angular/forms';
import { TSIconModule, TsButtonModule, TsSnackbarService } from 'tsui';
import { MatDividerModule } from '@angular/material/divider';
import { StakeholderItemComponent } from './stakeholder-item/stakeholder-item.component';
import { Approval, ApprovalStatus } from '../approvals.types';
import { LegacyTasksTabService } from '../../legacy-tasks/services/tasks.service';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { TaskApprovalComponent } from '../../tasks/task-details/task-approval/task-approval.component';
import { User } from 'app/core/models';
import { UserState } from 'app/state/user/state';
import { FeatureFlag, NewPermission, Permission } from 'app/core/enums';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';

@Component({
  selector: 'app-notify-stakeholders-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatLegacyDialogModule,
    FormsModule,
    TsButtonModule,
    MatDividerModule,
    TSIconModule,
    StakeholderItemComponent,
    TaskApprovalComponent,
  ],
  template: ` <div class="notify-stakeholder-modal">
    <div class="notify-stakeholder-modal__header">
      <span>Approval Request</span>
      <ts-icon class="cursor-pointer" icon="close" (click)="cancel()"></ts-icon>
    </div>
    <div class="notify-stakeholder-modal__content">
      <ng-container *ngIf="task.approvals.length; else emptyMessage">
        <app-stakeholder-item
          *ngFor="let approval of task.approvals"
          [approval]="approval"
          [managementPermission]="hasApprovalPermission"
          (resendToApproval)="onResendToApproval($event)"
          (removeApproval)="onRemoveApproval($event)"
        ></app-stakeholder-item>
      </ng-container>

      <ng-template #emptyMessage>
        <div class="flex justify-center items-center h-64" *ngIf="!task.approvals.length">
          <div class="text-center">
            <p class="mt-4 text-gray-300 text-3xl">No stakeholders selected</p>
          </div>
        </div>
      </ng-template>
    </div>
    <mat-divider></mat-divider>
    <div class="notify-stakeholder-modal__footer" [ngClass]="{ 'justify-end': !hasApprovalPermission }">
      <task-approval
        layout="compact"
        [approvals]="task.approvals || []"
        [users]="users"
        (addApprover)="onAddApprover($event)"
        (removeApprover)="onRemoveApproval($event)"
        *ngIf="hasApprovalPermission"
      >
      </task-approval>
      <div class="action-buttons flex gap-2">
        <ts-button (click)="cancel()" color="accent" type="stroked">Cancel</ts-button>
        @if (!isApproved()) {
          <ts-button (click)="resendToAll()" color="primary" [loading]="sendingToAll">Send Request</ts-button>
        } @else {
          <ts-button (click)="close()" color="primary" [loading]="close()">Finish</ts-button>
        }
      </div>
    </div>
  </div>`,
  styleUrls: ['./notify-stakeholders-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotifyStakeholdersModalComponent extends OnDestroyMixin implements OnInit {
  task: Task;
  users: User[] = [];
  sendingToAll = false;
  approvalResent = false;

  constructor(
    private matDialogRef: MatDialogRef<NotifyStakeholdersModalComponent>,
    private taskService: LegacyTasksTabService,
    private snackbarService: TsSnackbarService,
    private store: Store,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public data: { task: Task; users: User[] },
  ) {
    super();
  }

  get hasApprovalPermission(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.AGGREGATED_TASK_LISTS_EDIT));
    }

    return this.store.selectSnapshot(UserState.hasPermission(Permission.TASK_LIST_APPROVALS));
  }

  ngOnInit(): void {
    this.task = this.data.task;
    this.users = this.data.users;
  }

  cancel(): void {
    this.matDialogRef.close(this.approvalResent ? this.task : undefined);
  }

  isApproved(): boolean {
    return this.task.approvals.every((approval) => approval.status === ApprovalStatus.APPROVED);
  }

  close(): void {
    this.matDialogRef.close(this.task);
  }

  resendToAll(): void {
    this.sendingToAll = true;
    this.taskService
      .notifyAllTaskApprovers(this.task.task_list_id, this.task.id)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.task.approvals = this.task.approvals.map((approval) => {
          if (approval.status === ApprovalStatus.NONE || approval.status === ApprovalStatus.NOT_SENT) {
            approval.status = ApprovalStatus.NOT_YET;
          }
          return approval;
        });

        this.matDialogRef.close(this.task);
      });
  }

  onResendToApproval(approval: Approval): void {
    this.taskService
      .notifyTaskApprover(this.task.task_list_id, this.task.id, approval)
      .pipe(untilComponentDestroyed(this))
      .subscribe((approval) => {
        this.task.approvals = this.task.approvals.map((a) => (a.id === approval.id ? approval : a));
        this.approvalResent = true;
        this.cdr.detectChanges();
        this.snackbarService.open('Approval request resent successfully', 'success');
      });
  }

  onRemoveApproval(approval: Approval): void {
    this.taskService
      .removeApproverFromTask(this.task.task_list_id, this.task.id, approval)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.task.approvals = this.task.approvals.filter((a) => a.id !== approval.id);
        this.cdr.detectChanges();
        this.snackbarService.open('Approval deleted successfully', 'success');
      });
  }

  onAddApprover(approval: Approval) {
    this.taskService
      .addApproverToTask(this.task.task_list_id, this.task.id, approval)
      .pipe(untilComponentDestroyed(this))
      .subscribe((approval) => {
        this.task.approvals.push(approval);
        this.cdr.detectChanges();
      });
  }
}
